// ./client/src/api/messageAPI.js

import axiosInstance from '../utilities/axiosInstance';

const messageAPI = {
  // ============================================================================
  // FCM TOKEN MANAGEMENT
  // ============================================================================

  // Register FCM token
  registerToken: async (token) => {
    try {
      console.log('📤 [Frontend] Sending FCM token:', token?.substring(0, 20)); // ✅ Add this
      
      const response = await axiosInstance.post('/api/message/registerTokenAPI', {
        token
      });
      
      console.log('📥 [Frontend] Response:', response.data); // ✅ Add this
      return response.data;
    } catch (error) {
      console.error('❌ [Frontend] Error:', error.response?.data); // ✅ Add this
      return error?.response?.data || { success: false, error: `FCM registration failed: ${error.message}` };
    }
  },

  // Remove FCM token
  removeToken: async (userId) => {
    try {
      const response = await axiosInstance.delete(`/api/message/removeToken/${userId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove token: ${error.message}` };
    }
  },

  // Get all users with registered tokens
  getRegisteredUsers: async () => {
    try {
      const response = await axiosInstance.get('/api/message/users');
      return response.data; // {users}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get users: ${error.message}` };
    }
  },

  // ============================================================================
  // SEND MESSAGES
  // ============================================================================

  // Send message to a single user
  sendMessage: async (messageData) => {
    try {
      const response = await axiosInstance.post('/api/message/sendMessageAPI', messageData);
      return response.data; // {success, messageId, firebaseMessageId, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to send message: ${error.message}` };
    }
  },

  // Send multicast message to all registered users
  sendMulticast: async (adminId, adminName, title, body) => {
    try {
      const response = await axiosInstance.post('/api/message/sendMulticast', {
        adminId,
        adminName,
        title,
        body
      });
      return response.data; // {success, totalSentMessages}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to send multicast: ${error.message}` };
    }
  },

  // ============================================================================
  // GET MESSAGES
  // ============================================================================

  // Get messages between two users (marks as seen)
  getMessagesAndMarkSeen: async (senderId, receiverId, params = {}) => {
    try {
      const response = await axiosInstance.get(
        `/api/message/readUserMessages/${senderId}/${receiverId}`,
        { params }
      );
      return response.data; // {success, messages, totalMessages, unseenCount, currentPage, totalPages}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get messages: ${error.message}` };
    }
  },

  // Get messages without marking as seen
  getMessagesNoMarkSeen: async (senderId, receiverId, params = {}) => {
    try {
      const response = await axiosInstance.get(
        `/api/message/readSelfMessages/${senderId}/${receiverId}`,
        { params }
      );
      return response.data; // {success, messages, totalMessages, unseenCount, currentPage, totalPages}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get messages: ${error.message}` };
    }
  },

  // Get detailed message information
  getMessageInfo: async (messageId) => {
    try {
      const response = await axiosInstance.get(`/api/message/messageInfo/${messageId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get message info: ${error.message}` };
    }
  },

  // ============================================================================
  // UNREAD COUNTS
  // ============================================================================

  // Get unread count for a user
  getUnreadCount: async (userId) => {
    try {
      const response = await axiosInstance.get(`/api/message/unread/${userId}`);
      return response.data; // {success, unreadCount}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get unread count: ${error.message}` };
    }
  },

  // Get users with their unread message counts
  getUsersWithUnreadCounts: async (receiverId, users) => {
    try {
      const response = await axiosInstance.post('/api/message/usersWithUnreadCounts', {
        receiverId,
        users
      });
      return response.data; // {success, users, totalUnreadCount, usersWithUnreadMessages, countsBySender}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get unread counts: ${error.message}` };
    }
  },

  // ============================================================================
  // MESSAGE STATUS
  // ============================================================================

  // Mark single message as read
  markMessageAsRead: async (messageId, userId) => {
    try {
      const response = await axiosInstance.put(`/api/message/markAsRead/${messageId}`, {
        userId
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to mark as read: ${error.message}` };
    }
  },

  // Mark all messages as read for a user
  markAllAsRead: async (userId) => {
    try {
      const response = await axiosInstance.put(`/api/message/markAllAsRead/${userId}`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to mark all as read: ${error.message}` };
    }
  },

  // Update message delivery status
  updateMessageStatus: async (messageId, status, userId) => {
    try {
      const response = await axiosInstance.put(`/api/message/updateStatus/${messageId}`, {
        status,
        userId
      });
      return response.data; // {success, message, deliveryStatus}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update status: ${error.message}` };
    }
  },

  // Update multiple message status
  updateMultipleMessageStatus: async (messageIds, status, userId) => {
    try {
      const response = await axiosInstance.put('/api/message/updateMultipleStatus', {
        messageIds,
        status,
        userId
      });
      return response.data; // {success, message, modifiedCount}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update status: ${error.message}` };
    }
  },

  // ============================================================================
  // DELETE MESSAGES
  // ============================================================================

  // Soft delete a message
  deleteMessage: async (messageId, userId) => {
    try {
      const response = await axiosInstance.delete(`/api/message/${messageId}`, {
        data: { userId }
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete message: ${error.message}` };
    }
  },

  // ============================================================================
  // ADMIN FUNCTIONS
  // ============================================================================

  // Validate all registered FCM tokens
  validateAllTokens: async (adminId) => {
    try {
      const response = await axiosInstance.post('/api/message/validateAllTokens', {
        adminId
      });
      return response.data; // {success, message, results}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to validate tokens: ${error.message}` };
    }
  },

  // Send test notification to all users
  testAllNotifications: async (adminId, testMessage) => {
    try {
      const response = await axiosInstance.post('/api/message/testAllNotifications', {
        adminId,
        testMessage
      });
      return response.data; // {success, message, results}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to test notifications: ${error.message}` };
    }
  },

  // Get registered tokens info
  getRegisteredTokensInfo: async (adminId) => {
    try {
      const response = await axiosInstance.get('/api/message/getRegisteredTokensInfo', {
        params: { adminId }
      });
      return response.data; // {success, totalRegistered, users}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get tokens info: ${error.message}` };
    }
  },

  // Sync tokens from database to memory
  syncTokensFromDB: async (adminId) => {
    try {
      const response = await axiosInstance.post('/api/message/syncTokensFromDB', {
        adminId
      });
      return response.data; // {success, message, tokensLoaded}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to sync tokens: ${error.message}` };
    }
  },
};

export default messageAPI;